import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';

import {
  EMPTY_SLOT,
  desiredChip,
  mountedChip,
  paintedChip,
  reduceChipSlot,
  type ChipKind,
  type ChipSlotState,
} from '../../../lib/chip-morph';
import { ChipMorphLayer } from './ChipMorphLayer';

/**
 * The runtime half of `lib/chip-morph.ts`: it decides who owns the slot, keeps a
 * live snapshot of each chip (its rect and its markup) so the outgoing one can
 * still be drawn a frame after React has unmounted it, and mounts the overlay
 * that does the morphing.
 */

/**
 * A chip as the overlay needs it: where it sat, what it contained, and the skin
 * of its capsule. The skin is read off the real element rather than restated in
 * the layer, so a design change to either chip is picked up by the morph for
 * free — and it has to be captured HERE, because by the time the outgoing chip
 * is being animated away it is no longer in the document to be measured.
 */
export type ChipSkin = { backgroundColor: string; borderColor: string; borderWidth: string };
/**
 * `content` is a DETACHED CLONE of the anchor, not its markup as a string. Cloning the node keeps
 * this a copy of the real element rather than a re-parse of its serialization — nothing has to be
 * turned back into DOM, so nothing can be lost or reinterpreted on the way.
 *
 * It is also what keeps the layer free of raw-HTML injection: a chip's markup is assembled from
 * user-influenced content (agent names, call state), and serializing it only to feed it back
 * through an HTML parser is an injection surface we get nothing for.
 */
export type ChipSnapshot = { rect: DOMRect; content: HTMLElement; skin: ChipSkin };

function snapshotOf(anchor: HTMLElement): ChipSnapshot | null {
  const capsule = anchor.firstElementChild as HTMLElement | null;
  if (!capsule) return null;
  const style = window.getComputedStyle(capsule);
  return {
    rect: anchor.getBoundingClientRect(),
    // Deep clone, because by the time the OUTGOING chip is animated it has left the document.
    content: anchor.cloneNode(true) as HTMLElement,
    skin: {
      backgroundColor: style.backgroundColor,
      borderColor: style.borderTopColor,
      borderWidth: style.borderTopWidth,
    },
  };
}

type ChipSlotApi = {
  state: ChipSlotState;
  anchors: React.MutableRefObject<Partial<Record<ChipKind, HTMLElement | null>>>;
  snapshots: React.MutableRefObject<Partial<Record<ChipKind, ChipSnapshot>>>;
  /** Measure a chip that is still in the document (the one being morphed INTO). */
  readAnchor: (kind: ChipKind) => ChipSnapshot | null;
  settle: () => void;
};

const ChipSlotContext = createContext<ChipSlotApi | null>(null);

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

export function ChipSlotProvider({
  pillEligible,
  children,
}: {
  pillEligible: boolean;
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(reduceChipSlot, EMPTY_SLOT);
  const anchors = useRef<Partial<Record<ChipKind, HTMLElement | null>>>({});
  const snapshots = useRef<Partial<Record<ChipKind, ChipSnapshot>>>({});

  // The slot holds only the pill now; the minimized-call island renders on its own so the two
  // coexist (see VoiceCallIsland).
  const want = desiredChip({ pillEligible });
  // Layout effect, not a passive one: dispatching after paint would show one
  // frame of the old occupant beside the new state of the app.
  useLayoutEffect(() => {
    dispatch({ type: 'occupy', kind: want, reducedMotion: prefersReducedMotion() });
  }, [want]);

  const settle = useCallback(() => dispatch({ type: 'settle' }), []);
  const readAnchor = useCallback((kind: ChipKind) => {
    const el = anchors.current[kind];
    return el ? snapshotOf(el) : null;
  }, []);
  const api = useMemo<ChipSlotApi>(
    () => ({ state, anchors, snapshots, readAnchor, settle }),
    [state, readAnchor, settle],
  );

  return (
    <ChipSlotContext.Provider value={api}>
      {children}
      <ChipMorphLayer />
    </ChipSlotContext.Provider>
  );
}

function useChipSlotApi(): ChipSlotApi | null {
  return useContext(ChipSlotContext);
}

export function useChipMorphInternals(): ChipSlotApi | null {
  return useChipSlotApi();
}

/**
 * Whether the slot currently has this chip in the DOM at all. A layout that
 * makes ROOM for the pill (mobile home gives it its own row) asks this so the
 * row keeps its height while the call owns the capsule — otherwise the page
 * below jumps every time a call is minimized or ended.
 */
export function useChipSlotHolds(kind: ChipKind): boolean {
  const api = useChipSlotApi();
  return api ? mountedChip(api.state) === kind : true;
}

/**
 * What a chip component needs to take part in the slot.
 *
 * `mounted` false → render nothing. `painted` false → render, but hidden: the
 * overlay is drawing this chip's capsule right now, and the real one has to stay
 * laid out so its rect is measurable and the header's box never collapses.
 *
 * Outside a provider (tests, isolated stories) both are true and the chip
 * behaves exactly as it did before the slot existed.
 */
export function useChipSlot(kind: ChipKind): {
  mounted: boolean;
  painted: boolean;
  ref: (el: HTMLElement | null) => void;
} {
  const api = useChipSlotApi();
  const state = api?.state ?? null;
  const elRef = useRef<HTMLElement | null>(null);

  // Keyed on the anchors REF (stable for the provider's lifetime), never on the
  // api object — that one changes identity on every state change, and a ref
  // callback that changes identity is detached and re-attached with it, which
  // for a live call means churning the registration once a second.
  const anchorsRef = api?.anchors;
  const ref = useCallback(
    (el: HTMLElement | null) => {
      elRef.current = el;
      if (anchorsRef) anchorsRef.current[kind] = el;
    },
    [anchorsRef, kind],
  );

  // Refreshed on every render of the chip — so when the call ends and the island
  // unmounts, the layer already holds the frame it has to morph away from.
  useLayoutEffect(() => {
    const el = elRef.current;
    if (!api || !el) return;
    if (paintedChip(api.state) !== kind) return;
    const snapshot = snapshotOf(el);
    if (snapshot) api.snapshots.current[kind] = snapshot;
  });

  if (!state) return { mounted: true, painted: true, ref };
  return { mounted: mountedChip(state) === kind, painted: paintedChip(state) === kind, ref };
}
